import {
  IconCalendarMonth,
  IconFlame,
  IconToolsKitchen2,
} from "@tabler/icons-react";
import { format, max, min } from "date-fns";
import { useLocale, useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { Img } from "@/components/image";
import { getDateLocale } from "@/lib/helpers/diet-dates";
import { cn } from "@/lib/utils";

const SummaryBadge = ({
  icon,
  children,
  "data-pw": dataPw,
}: {
  icon: ReactNode;
  children: ReactNode;
  "data-pw"?: string;
}) => (
  <span
    className="inline-flex items-center gap-1 rounded-lg border border-brand-grey2 bg-brand-white px-2 py-1.5 text-brand-grey5 text-xs"
    data-pw={dataPw}
  >
    {icon}
    {children}
  </span>
);

type SummaryDietCardProps = {
  name: string;
  cateringName: string;
  imageUrl: string;
  days: number;
  kcal: number;
  meals: number;
  // Delivery days of the position. When passed (and non-empty), the calendar
  // badge shows the date range ("11 mar - 13 mar") instead of the day count:
  // the checkout sidebar asks for the range, the configuration summary keeps
  // the count.
  dates?: Array<Date | string>;
  // Lets the checkout sidebar drop the grey fill and the padding it already
  // gets from the surrounding product row.
  className?: string;
};

// Top block of the summary: diet thumbnail, name and catering, plus the diet
// parameters shown as compact badges instead of a stacked key/value list.
export const SummaryDietCard = ({
  name,
  cateringName,
  imageUrl,
  days,
  kcal,
  meals,
  dates,
  className,
}: SummaryDietCardProps) => {
  const t = useTranslations();
  const locale = useLocale();

  // Same wording as the cart listing tag, so both views read identically.
  const dateRangeLabel = dates?.length
    ? t("common.daysRangeSimple", {
        from: format(min(dates), "dd MMM", { locale: getDateLocale(locale) }),
        to: format(max(dates), "dd MMM", { locale: getDateLocale(locale) }),
      })
    : null;

  return (
    <div className={cn("flex flex-col gap-2 bg-brand-grey1 p-4", className)}>
      <div className="flex items-start gap-2">
        <Img
          alt={name}
          className="size-12 shrink-0 rounded-xl object-cover"
          height={48}
          src={imageUrl}
          width={48}
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-base text-brand-green1-300">
            {name}
          </span>
          <span className="truncate text-brand-grey5 text-sm">
            {cateringName}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        <SummaryBadge
          data-pw="days-quantity"
          icon={<IconCalendarMonth className="size-4" stroke={1.5} />}
        >
          {dateRangeLabel ?? t("common.days", { count: days })}
        </SummaryBadge>
        <SummaryBadge
          data-pw="calories-summary"
          icon={<IconFlame className="size-4" stroke={1.5} />}
        >
          {t("common.kcal", { kcal })}
        </SummaryBadge>
        <SummaryBadge
          data-pw="meals-summary"
          icon={<IconToolsKitchen2 className="size-4" stroke={1.5} />}
        >
          {t("common.meals", { meals })}
        </SummaryBadge>
      </div>
    </div>
  );
};
